import React, { useState, useEffect, useRef } from 'react';

// html-to-image is served from /public so the iframe loads it same-origin and instantly.
// The parent can't reach into the sandboxed iframe DOM, so the library has to live inside it.
// Build URL at render time so it works in dev (localhost:3000) and prod (same origin).
const IMG_LIB_PATH = '/html-to-image.js';

function buildIframeScript(libUrl: string): string {
  return `
<script src="${libUrl}"></script>
<script>
(function(){
  var lastReported = 0;
  function report(){
    var h = document.documentElement.scrollHeight || document.body.scrollHeight || 0;
    // 1px tolerance — subpixel font rendering or late image decodes cause
    // ±1px jitter that would otherwise spam the parent's setState.
    if (Math.abs(h - lastReported) < 2) return;
    lastReported = h;
    window.parent.postMessage({type:'iframe-resize',id:window.__iframeId,height:h},'*');
  }
  // Fire on the standard milestones. The setTimeouts guard against a race
  // where the parent's message listener attaches after our initial 'load' event.
  window.addEventListener('DOMContentLoaded', report);
  window.addEventListener('load', report);
  setTimeout(report, 100);
  setTimeout(report, 500);
  setTimeout(report, 1500);
  // ResizeObserver keeps height in sync if the content reflows later
  // (e.g. fonts finish loading, images decode, dynamic content shifts).
  if (typeof ResizeObserver !== 'undefined') {
    var ro = new ResizeObserver(report);
    if (document.documentElement) ro.observe(document.documentElement);
    if (document.body) ro.observe(document.body);
  }

  // Wait for html-to-image to finish loading. The library is local and ~20KB so this is
  // near-instant, but we still defend against the user clicking before parse completes.
  function waitForLib(cb, remainingMs){
    if (window.htmlToImage) { cb(null); return; }
    if (remainingMs <= 0) { cb(new Error('html-to-image failed to load')); return; }
    setTimeout(function(){ waitForLib(cb, remainingMs - 50); }, 50);
  }

  window.addEventListener('message', function(e){
    if (!e.data || e.data.id !== window.__iframeId) return;
    if (e.data.type === 'capture') {
      waitForLib(function(err){
        if (err) {
          window.parent.postMessage({type:'capture-error',id:window.__iframeId,error:err.message},'*');
          return;
        }
        window.htmlToImage.toPng(document.body, {
          backgroundColor: '#ffffff',
          pixelRatio: 2,
          cacheBust: true,
        }).then(function(dataUrl){
          window.parent.postMessage({type:'capture-result',id:window.__iframeId,dataUrl:dataUrl},'*');
        }).catch(function(err){
          window.parent.postMessage({type:'capture-error',id:window.__iframeId,error:String(err && err.message || err)},'*');
        });
      }, 5000);
    }
  });
})();
</script>`;
}

function dataUrlToBlob(dataUrl: string): Blob {
  const [meta, b64] = dataUrl.split(',');
  const mime = meta.match(/:(.*?);/)?.[1] || 'image/png';
  const bin = atob(b64);
  const len = bin.length;
  const arr = new Uint8Array(len);
  for (let i = 0; i < len; i++) arr[i] = bin.charCodeAt(i);
  return new Blob([arr], { type: mime });
}

export function HtmlIframe({ content }: { content: string }) {
  const [height, setHeight] = useState(400);
  const [busy, setBusy] = useState<'download' | 'copy' | null>(null);
  const [hint, setHint] = useState<string | null>(null);
  const id = useRef(`hif-${Math.random().toString(36).slice(2)}`);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const pendingActionRef = useRef<'download' | 'copy' | null>(null);

  useEffect(() => {
    function onMsg(e: MessageEvent) {
      // sandbox="allow-scripts" without allow-same-origin gives the iframe a null
      // origin, which browsers report inconsistently as 'null' or '' on the postMessage
      // event. The real defense is the per-instance id check below — but we still
      // refuse messages from the parent's own origin so a same-origin injection can't
      // spoof us. (Cross-origin iframes that don't share our parent origin can't reach
      // this listener via window.parent at all.)
      if (e.origin && e.origin !== 'null' && e.origin === window.location.origin) return;
      if (e.data?.id !== id.current) return;
      if (e.data.type === 'iframe-resize') {
        setHeight(Math.max(200, (e.data.height as number) + 24));
      } else if (e.data.type === 'capture-result') {
        const dataUrl = e.data.dataUrl as string;
        const action = pendingActionRef.current;
        pendingActionRef.current = null;
        if (action === 'download') {
          const a = document.createElement('a');
          a.href = dataUrl;
          a.download = `chart-${Date.now()}.png`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          setHint('Downloaded');
        } else if (action === 'copy') {
          try {
            const blob = dataUrlToBlob(dataUrl);
            navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })])
              .then(() => setHint('Copied'))
              .catch(() => setHint('Copy not supported in this browser'));
          } catch {
            setHint('Copy not supported in this browser');
          }
        }
        setBusy(null);
        setTimeout(() => setHint(null), 2500);
      } else if (e.data.type === 'capture-error') {
        pendingActionRef.current = null;
        setBusy(null);
        setHint(`Error: ${e.data.error}`);
        setTimeout(() => setHint(null), 3500);
      }
    }
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, []);

  const requestCapture = (action: 'download' | 'copy') => {
    if (busy) return;
    const win = iframeRef.current?.contentWindow;
    if (!win) return;
    pendingActionRef.current = action;
    setBusy(action);
    setHint(action === 'download' ? 'Generating image…' : 'Capturing…');
    win.postMessage({ type: 'capture', id: id.current }, '*');
  };

  const isFullDoc = content.trimStart().startsWith('<!') || content.trimStart().toLowerCase().startsWith('<html');
  const idScript = `<script>window.__iframeId='${id.current}';</script>`;
  // The iframe is null-origin (sandbox without allow-same-origin), so relative URLs don't
  // resolve to the parent's origin. Use the parent's full origin to load the library.
  const libUrl = (typeof window !== 'undefined' ? window.location.origin : '') + IMG_LIB_PATH;
  const iframeScript = buildIframeScript(libUrl);

  let srcDoc: string;
  if (isFullDoc) {
    const injected = idScript + iframeScript;
    srcDoc = content.includes('</body>')
      ? content.replace('</body>', injected + '</body>')
      : content + injected;
  } else {
    srcDoc = `<!DOCTYPE html><html><head><meta charset="utf-8">
<style>body{margin:0;padding:12px;font-family:sans-serif;background:#fff;color:#111827;}</style>
${idScript}</head><body>${content}${iframeScript}</body></html>`;
  }

  const buttonStyle: React.CSSProperties = {
    padding: '4px 10px',
    fontSize: 12,
    border: '1px solid #e5e7eb',
    borderRadius: 5,
    background: '#fff',
    color: '#6b7280',
    cursor: busy ? 'not-allowed' : 'pointer',
    opacity: busy ? 0.6 : 1,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
  };

  return (
    <div style={{ margin: '8px 0' }}>
      <iframe
        ref={iframeRef}
        srcDoc={srcDoc}
        sandbox="allow-scripts"
        style={{ width: '100%', height, border: 'none', borderRadius: 8, background: '#fff', display: 'block' }}
        title="Chart"
      />
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
        <button onClick={() => requestCapture('download')} disabled={!!busy} style={buttonStyle} title="Download as PNG">
          <svg width="12" height="12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5 5-5M12 15V3" />
          </svg>
          Download
        </button>
        <button onClick={() => requestCapture('copy')} disabled={!!busy} style={buttonStyle} title="Copy image to clipboard">
          <svg width="12" height="12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
          Copy image
        </button>
        {hint && <span style={{ fontSize: 12, color: '#6b7280' }}>{hint}</span>}
      </div>
    </div>
  );
}

export function toPlainText(markdown: string): string {
  return markdown
    .replace(/^#{1,6}\s+(.+)$/gm, '$1')
    .replace(/\*\*(.+?)\*\*/gs, '$1')
    .replace(/__(.+?)__/gs, '$1')
    .replace(/\*(.+?)\*/gs, '$1')
    .replace(/_(.+?)_/gs, '$1')
    .replace(/```[\w]*\n?([\s\S]*?)```/g, '$1')
    .replace(/`(.+?)`/g, '$1')
    .replace(/^\s*[-*]\s+/gm, '• ')
    .replace(/\|(.+)\|/g, (_: string, row: string) =>
      row.split('|').map((c: string) => c.trim()).filter(Boolean).join('   ')
    )
    .replace(/^\|[-:\s|]+\|$/gm, '')
    .replace(/\[(.+?)\]\((.+?)\)/g, '$1')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}
